import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface LoyaltyProfile {
  id: string;
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  points_balance: number;
  total_points_earned: number;
  status: 'pendiente' | 'activo' | 'suspendido';
  member_number: string | null;
  instagram_claimed: boolean;
  welcome_points_claimed: boolean;
  registration_source: string | null;
  created_at: string;
  updated_at: string;
}

interface PointsHistoryItem {
  id: string;
  points: number;
  type: string;
  description: string;
  purchase_amount: number | null;
  created_at: string;
}

interface LoyaltyReward {
  id: string;
  name: string;
  description: string | null;
  points_required: number;
  category: string;
  is_active: boolean;
  image_url: string | null;
}

export const useLoyaltyProfile = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<LoyaltyProfile | null>(null);
  const [pointsHistory, setPointsHistory] = useState<PointsHistoryItem[]>([]);
  const [rewards, setRewards] = useState<LoyaltyReward[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = async () => {
    if (!user) {
      setProfile(null);
      setLoading(false);
      return;
    }

    try {
      const { data, error: fetchError } = await supabase
        .from('loyalty_profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (fetchError) throw fetchError;
      
      // Type cast to handle the enum type
      if (data) {
        setProfile(data as unknown as LoyaltyProfile);
      } else {
        setProfile(null);
      }
    } catch (err) {
      console.error('Error fetching loyalty profile:', err);
      setError('Error al cargar el perfil de fidelización');
    } finally {
      setLoading(false);
    }
  };

  const fetchPointsHistory = async () => {
    if (!user) return;

    try {
      const { data, error: fetchError } = await supabase
        .from('points_history')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (fetchError) throw fetchError;
      setPointsHistory(data as PointsHistoryItem[] || []);
    } catch (err) {
      console.error('Error fetching points history:', err);
    }
  };

  const fetchRewards = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('loyalty_rewards')
        .select('*')
        .eq('is_active', true)
        .order('points_required', { ascending: true });

      if (fetchError) throw fetchError;
      setRewards(data as LoyaltyReward[] || []);
    } catch (err) {
      console.error('Error fetching rewards:', err);
    }
  };

  useEffect(() => {
    fetchProfile();
    fetchPointsHistory();
    fetchRewards();
  }, [user]);

  // Real-time subscription for profile updates
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('loyalty-profile-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'loyalty_profiles',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          fetchProfile();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'points_history',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          fetchPointsHistory();
          fetchProfile(); // Also refresh profile to get updated balance
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const getAvailableRewards = () => {
    if (!profile) return [];
    return rewards.filter(r => r.points_required <= profile.points_balance);
  };

  return {
    profile,
    pointsHistory,
    rewards,
    availableRewards: getAvailableRewards(),
    loading,
    error,
    refetch: () => {
      fetchProfile();
      fetchPointsHistory();
      fetchRewards();
    },
  };
};
